/* ──────────────────────────────────────────────────────────────────────
 *  My Impact Page — Impact Signature
 *
 *  Layout:
 *  - DimensionRadar (full-width signature card)
 *  - Two-column grid of abbreviated panels:
 *      Row 1: Mind & Voice (left)  | Body & Heart (right)
 *      Row 2: Wings (left)         | Roots (right)
 *
 *  Clicking a card opens an ImpactPreviewModal (read-only overlay)
 *  so the user can browse items without leaving this page.
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import DimensionRadar from '../../Components/DimensionRadar/DimensionRadar';
import type { DimKey } from '../../Components/DimensionRadar/dimensionConfig';
import type { MedalTier } from '../../Components/DimensionPanelHeader/DimensionPanelHeader';
import DimensionAdvicePanel from '../../Components/DimensionAdvicePanel/DimensionAdvicePanel';
import DimensionActionModal from '../../Components/DimensionActionModal/DimensionActionModal';
import type { AdvanceAction } from '../../Data/Onboarding/dimensionAdvanceActions';
import { useApiImpactSignature } from '../../api/useApiImpactSignature';
import { useApiSecurityData } from '../../api/useApiSecurityData';
import { useApiOnboarding } from '../../api/useApiOnboarding';
import ContentPanel from '../../Components/ContentPanel/ContentPanel';
import ActiveRolesPanel from '../../Components/ActiveRolesPanel/ActiveRolesPanel';
import ResourcesPanel from '../../Components/ResourcesPanel/ResourcesPanel';
import VerificationPanel from '../../Components/VerificationPanel/VerificationPanel';
import GrowthResourceMatches from '../../Components/DimensionRadar/GrowthResourceMatches';
import ImpactPreviewModal from '../../Components/ImpactPreviewModal/ImpactPreviewModal';
import type { ImpactPreviewTarget } from '../../Components/ImpactPreviewModal/ImpactPreviewModal';
import ExplainRadarModal, { RADAR_EXPLAIN_SEEN_KEY } from '../../Components/ExplainRadarModal/ExplainRadarModal';
import styles from './MyImpactPage.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface MyImpactPageProps {
  /** Filtered project data rows (same data MyProjectsPage receives). */
  data: any[];
  currentUserGuid: string;
  debugMode?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function MyImpactPage({ data, currentUserGuid, debugMode = false }: MyImpactPageProps) {
  const { t } = useAppTranslation('pages');

  /* ---- Impact Signature data ---- */
  const { tiers: impactTiers } = useApiImpactSignature(currentUserGuid);

  /* ---- Platform standing (rung + member-since) ----
     Folded in from the retired /memberships/life4earth standing view
     (architect ratification 2026-07-09 evening; §7.3/§7.6 amended): the
     signature page is now the one home for "me on Life4Earth". */
  const {
    archetype,
    achievedMilestones,
    recordMilestone,
    loading: onboardingLoading,
  } = useApiOnboarding(currentUserGuid);
  const rungLabel = useMemo(() => {
    const RUNG: Record<string, string> = {
      explorer: 'explorer',
      contributor: 'contributor',
      leader: 'leader',
      governor: 'governor',
    };
    const rung = t(`actionCenter.inbox.rung.${(archetype && RUNG[archetype]) || 'explorer'}`);
    return `${t('actionCenter.inbox.rail.base')} ${rung}`;
  }, [archetype, t]);

  /* Current medal for a dimension (defaults to 'none' until data loads). */
  const medalFor = (d: DimKey): MedalTier => (impactTiers?.[d] as MedalTier) ?? 'none';

  /* ---- Dimension action modal (explainer + route CTA) ---- */
  const [actionTarget, setActionTarget] = useState<{ dim: DimKey; action: AdvanceAction } | null>(null);
  const openAction = useCallback((dim: DimKey, action: AdvanceAction) => {
    setActionTarget({ dim, action });
  }, []);

  /* ---- Community GUIDs (for DimensionRadar context) ---- */
  const { communityMemberships, securityUsers } = useApiSecurityData();

  /* ---- Resolve current user row for VerificationPanel ---- */
  // DEV-906: VerificationPanel runs the synthetic milestone evaluator
  // against the current user's row (for is_two_factor_enabled,
  // eula_accepted_at, eula_version_accepted, profile completeness).
  // We resolve once here and thread the row down via prop.
  const currentUserRow = useMemo(() => {
    const guid = (currentUserGuid || '').toUpperCase();
    if (!guid) return null;
    for (const u of securityUsers || []) {
      if (String((u as any)?.user_guid || '').toUpperCase() === guid) {
        return u as Record<string, any>;
      }
    }
    return null;
  }, [securityUsers, currentUserGuid]);
  const userCommunityGuids = useMemo(
    () => {
      if (!currentUserGuid) return [];
      return communityMemberships
        .filter((m: any) =>
          (m.user_guid || '').toUpperCase() === currentUserGuid.toUpperCase()
          // DEV-1414: the API now returns pending/invited/suspended rows too;
          // radar context means BELONGING, so gate on status === 'active'
          // (the old !== 'removed' check matched a status that never existed).
          && String(m.status || 'active').toLowerCase() === 'active'
        )
        .map((m: any) => m.community_entity_guid as string)
        .filter(Boolean);
    },
    [communityMemberships, currentUserGuid],
  );

  /* ---- Preview modal state ---- */
  const [previewTarget, setPreviewTarget] = useState<ImpactPreviewTarget | null>(null);

  const handlePreview = useCallback((target: ImpactPreviewTarget) => {
    setPreviewTarget(target);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewTarget(null);
  }, []);

  /* ---- Active dimension selection (drives recommendation mode) ---- */
  const [activeDimension, setActiveDimension] = useState<DimKey | null>(null);

  const handleDimensionSelect = useCallback((dim: DimKey | null) => {
    setActiveDimension(dim);
  }, []);

  /* NOTE (Slice 1, HANDOFF_MYIMPACT_SUGGESTION_REMOVAL): the per-panel
     recommendation gating (PANEL_DIMENSIONS / isPanelActive /
     hasRecommendationContent) was removed as dead code — nothing consumed it.
     `activeDimension` is still captured from DimensionRadar for a future
     slice; today it is write-only. */

  /* ---- Beat-2 reveal (§7.16, chunk B slice D) ----
     The one-time ExplainRadarModal fires from the SERVER `radar_explained`
     milestone (never localStorage-triggered); a USER-namespaced belt only
     bridges the refetch gap after recording (DEV-1443 rule — the old
     un-namespaced key let one user's visit suppress another's reveal).
     `revealArrival` marks the SAME first visit: the radar's first-render
     draw-in animation keys on it (reduced-motion collapses via CSS). */
  const beltKey = `${RADAR_EXPLAIN_SEEN_KEY}:${(currentUserGuid || '').toUpperCase()}`;
  const [showRadarExplain, setShowRadarExplain] = useState(false);
  const [revealArrival, setRevealArrival] = useState(false);
  const revealFiredRef = useRef(false);
  useEffect(() => {
    if (revealFiredRef.current || onboardingLoading || !currentUserGuid) return;
    if (achievedMilestones.has('radar_explained')) return;
    try {
      if (window.localStorage.getItem(beltKey) === '1') return;
    } catch { /* privacy mode — server flag alone governs */ }
    revealFiredRef.current = true;
    setRevealArrival(true);
    setShowRadarExplain(true);
  }, [onboardingLoading, achievedMilestones, currentUserGuid, beltKey]);

  /* Manual re-open (the "?" affordance) — always allowed; §7.9-style
     resolved content stays reachable. Re-closing re-records (idempotent). */
  const handleOpenRadarExplain = useCallback(() => setShowRadarExplain(true), []);
  const handleCloseRadarExplain = useCallback(() => {
    setShowRadarExplain(false);
    try {
      window.localStorage.setItem(beltKey, '1');
    } catch { /* ignore */ }
    recordMilestone({ milestoneKey: 'radar_explained' }).catch(() => {});
  }, [beltKey, recordMilestone]);

  /* ---- Render ---- */
  return (
    <div className={styles.root}>
      {/* ── Scrollable body ── */}
      <div className={styles.body}>
        <div className={styles.bodyInner}>
          {/* Wide layout wrapper — on ≥1800px viewports, places signature
              and panel grid side-by-side. Below that threshold, the wrapper
              is transparent and both sections stack vertically as before. */}
          {/* Flat grid — all 5 items are direct children so CSS Grid can
               place them independently at each responsive breakpoint.
               Wide (≥1400): sig + 2×2 panels side-by-side
               Mid  (1000–1399): sig + 1×2 panels, remaining 1×2 below
               Narrow (<1000): single column stack */}
          <div className={styles.impactGrid}>
            <div
              className={`${styles.signatureCard} ${styles.areaSig} ${revealArrival ? styles.revealArrival : ''}`}
            >
              <div className={styles.signatureHeader}>
                <h2 className={styles.signatureTitle}>{t('impact.impactSignature')}</h2>
                {/* Standing line (from the retired standing view): rung +
                    platform member-since. Quiet — identity, not urgency. */}
                <span className={styles.standingLine}>
                  {rungLabel}
                  {(() => {
                    const iso = (currentUserRow as any)?.created_at;
                    const dt = iso ? new Date(iso) : null;
                    return dt && !Number.isNaN(dt.getTime())
                      ? ` · ${t('memberships.tray.memberSince', {
                          date: dt.toLocaleDateString(undefined, {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          }),
                        })}`
                      : null;
                  })()}
                </span>
                <button
                  className={styles.infoBtn}
                  onClick={handleOpenRadarExplain}
                  aria-label={t('impact.radarModalTitle')}
                  title={t('impact.radarModalTitle')}
                >
                  ?
                </button>
              </div>
              <div className={styles.signatureBody}>
                {/* ONE medal set (Skip correction 2026-07-09): DimensionRadar's
                    dashboard mode already renders the interactive dimension
                    medal-card grid as its right pane — at the summary layer's
                    full width that native two-pane layout IS the ratified
                    shape (radar left, 2×3 medal cards right). No duplicate
                    plain-medal column, no second status label here; the
                    overall status label lives on the rail card. */}
                <DimensionRadar
                  mode="dashboard"
                  debugMode={debugMode}
                  tiers={impactTiers ?? undefined}
                  communityGuids={userCommunityGuids}
                  onDimensionSelect={handleDimensionSelect}
                  /* DEV-1489 Slice C — gate growth acts by the viewer's rung.
                     Vocabulary bridge: the onboarding archetype ladder says
                     'leader', the impact-rung ladder says 'champion' (Skip's
                     2026-07-13 ruling). Map at the boundary so the radar only
                     ever sees the impact vocabulary. */
                  viewerRung={
                    archetype === 'leader'
                      ? 'champion'
                      : ((archetype as 'explorer' | 'contributor' | 'governor' | undefined) ?? 'explorer')
                  }
                />
              </div>
            </div>

            {/* GROWTH LAYER (§7.6-as-amended, ratified 2026-07-09 late):
                the six dimension recommendation panels sit BELOW the summary
                at every width — "here is your pattern → here are ways to
                grow it" — never beside it as equal peers. Canonical pair
                rows: Mind/Voice · Heart/Body · Wings/Roots. Layout only:
                reveal choreography + real medal data are chunk B's. */}

            {/* ── Mind (top-left) ── */}
            <div className={styles.areaP1}>
              <DimensionAdvicePanel dim="mind" medal={medalFor('mind')} onAction={openAction} />
            </div>

            {/* ── Voice (top-right) ── */}
            <div className={styles.areaP2}>
              <DimensionAdvicePanel dim="voice" medal={medalFor('voice')} onAction={openAction} />
            </div>

            {/* ── Wings (bottom-left) ── */}
            <div className={styles.areaP3}>
              <DimensionAdvicePanel dim="wings" medal={medalFor('wings')} onAction={openAction} />
            </div>

            {/* ── Roots (bottom-right) ── */}
            <div className={styles.areaP4}>
              <DimensionAdvicePanel dim="roots" medal={medalFor('roots')} onAction={openAction} />
            </div>

            {/* ── Heart (mid-left) ── */}
            <div className={styles.areaP5}>
              <DimensionAdvicePanel dim="heart" medal={medalFor('heart')} onAction={openAction} />
            </div>

            {/* ── Body (mid-right) ── */}
            <div className={styles.areaP6}>
              <DimensionAdvicePanel dim="body" medal={medalFor('body')} onAction={openAction} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Preview modal overlay ── */}
      {previewTarget && (
        <ImpactPreviewModal target={previewTarget} onClose={handleClosePreview} />
      )}

      {/* ── Dimension action modal (explainer + route CTA) ── */}
      {actionTarget && (
        <DimensionActionModal
          dim={actionTarget.dim}
          action={actionTarget.action}
          onClose={() => setActionTarget(null)}
        />
      )}

      {/* ── Explain Radar modal (auto on first visit, re-invocable via ?) ── */}
      {showRadarExplain && (
        <ExplainRadarModal onClose={handleCloseRadarExplain} />
      )}
    </div>
  );
}
